import React, { useState, useEffect } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';

const Footer = () => {
  const [email, setEmail] = useState('');
  const [isVisible, setIsVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const footerElement = document.getElementById('footer');
      if (footerElement) {
        const position = footerElement.getBoundingClientRect();
        if (position.top < window.innerHeight) {
          setIsVisible(true);
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check on initial render

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const handleEmailChange = (event) => {
    setEmail(event.target.value);
  };

  const handleFormSubmit = (event) => {
    event.preventDefault();
    if (email) {
      setIsSubmitted(true);
      // Animate success state then redirect
      setTimeout(() => {
        window.location.href = '/gift.pdf'; // Replace with actual PDF path
      }, 1000);
    } else {
      alert('Please enter a valid email address.');
    }
  };

  return (
    <FooterContainer id="footer" className={isVisible ? 'visible' : ''}>
      <FooterWave>
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1440 320">
          <path
            fill="#0099ff"
            fillOpacity="0.5"
            d="M0,64L48,80C96,96,192,128,288,128C384,128,480,96,576,85.3C672,75,768,85,864,112C960,139,1056,181,1152,181.3C1248,181,1344,139,1392,117.3L1440,96L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          ></path>
        </svg>
      </FooterWave>

      <FooterContent>
        <LeftSection>
          <SectionTitle>
            <GiftIcon />
            Here is a small gift
          </SectionTitle>
          <SubscribeContainer>
            <SubscribeForm onSubmit={handleFormSubmit} className={isSubmitted ? 'submitted' : ''}>
              {!isSubmitted ? (
                <>
                  <SubscribeInput
                    type="email"
                    placeholder="Enter your Email*"
                    value={email}
                    onChange={handleEmailChange}
                    required
                  />
                  <SubscribeButton type="submit">Claim Now</SubscribeButton>
                </>
              ) : (
                <SuccessMessage>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path
                      d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"
                      fill="currentColor"
                    />
                  </svg>
                  <span>Download starting...</span>
                </SuccessMessage>
              )}
            </SubscribeForm>
          </SubscribeContainer>
        </LeftSection>

        <MiddleSection>
          <Logo>DP</Logo>
          <Name>Dibyanjaya Panda</Name>
          <Tagline>Where Quality Meets Innovation</Tagline>
          <SocialLinks>
            <SocialLink href="#" aria-label="LinkedIn">
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.32 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.79M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z" />
              </svg>
            </SocialLink>
            <SocialLink href="#" aria-label="GitHub">
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2A10 10 0 0 0 2 12c0 4.42 2.87 8.17 6.84 9.5.5.08.66-.23.66-.5v-1.69c-2.77.6-3.36-1.34-3.36-1.34-.46-1.16-1.11-1.47-1.11-1.47-.91-.62.07-.6.07-.6 1 .07 1.53 1.03 1.53 1.03.87 1.52 2.34 1.07 2.91.83.09-.65.35-1.09.63-1.34-2.22-.25-4.55-1.11-4.55-4.92 0-1.11.38-2 1.03-2.71-.1-.25-.45-1.29.1-2.64 0 0 .84-.27 2.75 1.02.79-.22 1.65-.33 2.5-.33.85 0 1.71.11 2.5.33 1.91-1.29 2.75-1.02 2.75-1.02.55 1.35.2 2.39.1 2.64.65.71 1.03 1.6 1.03 2.71 0 3.82-2.34 4.66-4.57 4.91.36.31.69.92.69 1.85V21c0 .27.16.59.67.5C19.14 20.16 22 16.42 22 12A10 10 0 0 0 12 2z" />
              </svg>
            </SocialLink>
            <SocialLink href="#" aria-label="Twitter">
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M22.46 6c-.77.35-1.6.58-2.46.69.88-.53 1.56-1.37 1.88-2.38-.83.5-1.75.85-2.72 1.05C18.37 4.5 17.26 4 16 4c-2.35 0-4.27 1.92-4.27 4.29 0 .34.04.67.11.98C8.28 9.09 5.11 7.38 3 4.79c-.37.63-.58 1.37-.58 2.15 0 1.49.75 2.81 1.91 3.56-.71 0-1.37-.2-1.95-.5v.03c0 2.08 1.48 3.82 3.44 4.21a4.22 4.22 0 0 1-1.93.07 4.28 4.28 0 0 0 4 2.98 8.521 8.521 0 0 1-5.33 1.84c-.34 0-.68-.02-1.02-.06C3.44 20.29 5.7 21 8.12 21 16 21 20.33 14.46 20.33 8.79c0-.19 0-.37-.01-.56.84-.6 1.56-1.36 2.14-2.23z" />
              </svg>
            </SocialLink>
          </SocialLinks>
          <Copyright>© 2024 Dibyanjaya. All Rights Reserved.</Copyright>
        </MiddleSection>

        <RightSection>
          <SectionTitle>Thank You For Visiting</SectionTitle>
          <AnimationContainer>
            <iframe
              src="https://lottie.host/embed/483e0886-9d6d-4d4d-a1ae-17f70e43f3d0/Xl5AHMdoTg.lottie"
              title="Lottie Animation"
              frameBorder="0"
            ></iframe>
          </AnimationContainer>
          <ContactInfo>
            <ContactItem>
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
              </svg>
              <span>dibyanjayapanda25@gmail.com</span>
            </ContactItem>
            <ContactItem>
              <svg viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 11.5c-1.1 0-2-.9-2-2s.9-2 2-2 2 .9 2 2-.9 2-2 2zm6-1.8C18 6.57 15.35 4 12 4s-6 2.57-6 5.7c0 1.56.53 2.96 1.41 4.02L12 21l4.59-7.28c.88-1.06 1.41-2.46 1.41-4.02z" />
              </svg>
              <span>Balasore, Odisha</span>
            </ContactItem>
          </ContactInfo>
        </RightSection>
      </FooterContent>
    </FooterContainer>
  );
};

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const float = keyframes`
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-8px);
  }
`;

const pulse = keyframes`
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.05);
  }
`;

const checkmarkAnimation = keyframes`
  from {
    stroke-dashoffset: 100;
  }
  to {
    stroke-dashoffset: 0;
  }
`;

// Styled components
const FooterContainer = styled.footer`
  position: relative;
  background: linear-gradient(135deg, #131313 0%, #1f1f1f 100%);
  color: #fff;
  padding: 6rem 2rem 3rem;
  overflow: hidden;
  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.6s ease, transform 0.6s ease;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  @media (max-width: 768px) {
    padding: 5rem 1rem 2rem;
  }
`;

const FooterWave = styled.div`
  position: absolute;
  top: -10px;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
  transform: rotate(180deg);
  
  svg {
    position: relative;
    display: block;
    width: calc(100% + 1.3px);
    height: 65px;
    transform: rotateY(180deg);
  }
  
  path {
    fill: #0099ff;
    fill-opacity: 0.1;
    animation: ${float} 8s ease-in-out infinite;
  }
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  max-width: 1400px;
  margin: 0 auto;
  
  @media (max-width: 992px) {
    flex-direction: column;
    gap: 3rem;
    align-items: center;
    text-align: center;
  }
`;

const SectionTitle = styled.h4`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 8px;
  color: #fff;
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

const LeftSection = styled.div`
  flex: 1;
  max-width: 350px;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards 0.2s;
  
  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const GiftIcon = styled.div`
  position: relative;
  width: 24px;
  height: 24px;
  background-color: #ff6b6b;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${pulse} 2s infinite ease-in-out;
  
  &:before {
    content: "🎁";
    font-size: 12px;
  }
  
  &:after {
    content: "";
    position: absolute;
    top: -4px;
    left: 8px;
    width: 8px;
    height: 8px;
    background: #ff6b6b;
    border-radius: 50%;
  }
`;

const SubscribeContainer = styled.div`
  margin-top: 1.5rem;
`;

const SubscribeForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(4px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  
  &.submitted {
    background: rgba(21, 178, 61, 0.15);
    border-color: rgba(21, 178, 61, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const SubscribeInput = styled.input`
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(255, 255, 255, 0.05);
  color: #fff;
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: #0099ff;
    box-shadow: 0 0 0 2px rgba(0, 153, 255, 0.3);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const SubscribeButton = styled.button`
  padding: 1rem;
  border-radius: 8px;
  border: none;
  background: linear-gradient(135deg, #0099ff 0%, #4a6cf7 100%);
  color: white;
  font-weight: 600;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 7px 14px rgba(0, 153, 255, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const SuccessMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: #15b23d;
  padding: 1rem;
  
  svg {
    width: 24px;
    height: 24px;
    stroke-dasharray: 100;
    stroke-dashoffset: 100;
    animation: ${checkmarkAnimation} 0.6s forwards ease-in-out;
  }
`;

const MiddleSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards 0.4s;
`;

const Logo = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #0099ff 0%, #4a6cf7 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 1.5rem;
  margin-bottom: 1rem;
  box-shadow: 0 10px 20px rgba(0, 153, 255, 0.3);
  animation: ${float} 3s ease-in-out infinite;
`;

const Name = styled.h1`
  font-size: 1.75rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
  background: linear-gradient(to right, #fff, #0099ff);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Tagline = styled.p`
  font-size: 1rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 1.5rem;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const SocialLink = styled.a`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  
  svg {
    width: 20px;
    height: 20px;
    color: rgba(255, 255, 255, 0.7);
    transition: color 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    background: #0099ff;
    
    svg {
      color: white;
    }
  }
`;

const Copyright = styled.p`
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.5);
`;

const RightSection = styled.div`
  flex: 1;
  max-width: 350px;
  opacity: 0;
  animation: ${fadeIn} 1s ease-out forwards 0.6s;
  
  @media (max-width: 992px) {
    max-width: 100%;
  }
`;

const AnimationContainer = styled.div`
  height: 150px;
  margin-bottom: 1.5rem;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
  
  iframe {
    width: 100%;
    height: 100%;
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: rgba(255, 255, 255, 0.7);
  transition: color 0.3s ease;
  
  svg {
    width: 20px;
    height: 20px;
    color: #0099ff;
  }
  
  &:hover {
    color: #fff;
  }
  
  @media (max-width: 992px) {
    justify-content: center;
  }
`;

export default Footer;